export const NAVIGATION_ITEMS_MODE = {
  AUTOMATIC: 'automatic',
  CUSTOM: 'custom',
}

export const NAVIGATION_ITEM_TYPE = {
  COLLECTION: 'collection',
  LINK: 'link',
  MENU: 'menu',
  SHOP_ALL: 'shop_all',
  OCCASIONS: 'occasions',
}

export const NAVIGATION_VISIBILITY_MODE = {
  AUTOMATIC: 'automatic',
  MANUAL: 'manual',
}

export const TOP_LEVEL_PLACEMENT = 'top'

export const NAVIGATION_DESTINATION_MAX_LENGTH = 255
export const ANNOUNCEMENT_LABEL_MAX_LENGTH = 255

export function isSafeNavigationDestination(href) {
  if (typeof href !== 'string') return false

  const value = href.trim()
  if (value !== href) return false
  if (value.includes('\\')) return false

  if (/^\/(?![\\/])/.test(value)) {
    return true
  }

  if (!/^[\x21-\x7e]+$/.test(value)) return false
  if (!/^https?:\/\//i.test(value)) return false

  try {
    const url = new URL(value)
    const hostname = url.hostname.endsWith('.') ? url.hostname.slice(0, -1) : url.hostname
    const hasValidHostname = hostname.startsWith('[')
      ? hostname.endsWith(']')
      : hostname.split('.').every((label) => /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i.test(label))

    return ['http:', 'https:'].includes(url.protocol) && Boolean(hostname) && hasValidHostname
  } catch {
    return false
  }
}

let draftId = 0

export function createNavigationDraftId() {
  draftId += 1
  return `navigation-draft-${Date.now()}-${draftId}`
}

function ensureItemId(item) {
  return item?.id ?? createNavigationDraftId()
}

export function isShopAllDestination(href) {
  if (typeof href !== 'string') return false

  const value = href.trim()
  if (!value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return false

  return value.split(/[?#]/, 1)[0].replace(/\/+$/, '') === '/products'
}

function isLegacyShopAllLink(item) {
  return item?.type === NAVIGATION_ITEM_TYPE.LINK && isShopAllDestination(item?.href)
}

function navigationSystemType(item) {
  if (isLegacyShopAllLink(item)) {
    return NAVIGATION_ITEM_TYPE.SHOP_ALL
  }

  return [NAVIGATION_ITEM_TYPE.SHOP_ALL, NAVIGATION_ITEM_TYPE.OCCASIONS].includes(item?.type)
    ? item.type
    : null
}

function normalizeNode(item, sortOrder, { child = false } = {}) {
  const canonicalType = navigationSystemType(item) ?? item.type
  const type =
    child && canonicalType === NAVIGATION_ITEM_TYPE.MENU ? NAVIGATION_ITEM_TYPE.LINK : canonicalType
  const normalized = {
    ...item,
    id: ensureItemId(item),
    type,
    sort_order: sortOrder,
    is_enabled: item?.is_enabled ?? true,
    props: item?.props ?? {},
  }

  if (type === NAVIGATION_ITEM_TYPE.MENU) {
    normalized.href = null
    normalized.collection_id = null
    normalized.children = normalizeNavigationTree(item?.children ?? [], { child: true })
  } else {
    delete normalized.children
  }

  if (type !== NAVIGATION_ITEM_TYPE.COLLECTION) {
    normalized.collection_id = null
    delete normalized.collection
  }

  if (type !== NAVIGATION_ITEM_TYPE.LINK) {
    normalized.href = null
  }

  return normalized
}

export function normalizeNavigationTree(items, options = {}) {
  return (Array.isArray(items) ? items : []).map((item, index) =>
    normalizeNode(item, index, options)
  )
}

function promoteAutomaticNavigationItems(items) {
  const visibleItems = normalizeNavigationTree(items).filter((item) => item.is_enabled !== false)
  const existingShopAll = visibleItems.find((item) => item.type === NAVIGATION_ITEM_TYPE.SHOP_ALL)
  const existingOccasions = visibleItems.find(
    (item) => item.type === NAVIGATION_ITEM_TYPE.OCCASIONS
  )
  const existingItems = visibleItems.filter((item) =>
    [NAVIGATION_ITEM_TYPE.COLLECTION, NAVIGATION_ITEM_TYPE.LINK].includes(item.type)
  )

  return normalizeNavigationTree([
    existingShopAll
      ? { ...existingShopAll, is_enabled: true }
      : {
          id: createNavigationDraftId(),
          type: NAVIGATION_ITEM_TYPE.SHOP_ALL,
          label: null,
          href: null,
          collection_id: null,
          is_enabled: true,
          props: {},
        },
    ...existingItems,
    existingOccasions
      ? { ...existingOccasions, is_enabled: true }
      : {
          id: createNavigationDraftId(),
          type: NAVIGATION_ITEM_TYPE.OCCASIONS,
          label: null,
          href: null,
          collection_id: null,
          is_enabled: true,
          props: {},
        },
  ])
}

export function navigationItemsForCustomMode(
  items,
  sourceMode,
  { preserveAutomaticOrder = false } = {}
) {
  if (sourceMode === NAVIGATION_ITEMS_MODE.CUSTOM) {
    return normalizeNavigationTree(items)
  }

  if (preserveAutomaticOrder) {
    return normalizeNavigationTree(
      normalizeNavigationTree(items).filter((item) => item.is_enabled !== false)
    )
  }

  return promoteAutomaticNavigationItems(items)
}

function automaticNavigationItemKey(item) {
  const systemType = navigationSystemType(item)
  if (systemType) return `system:${systemType}`

  if (item?.type === NAVIGATION_ITEM_TYPE.LINK) {
    return `legacy-link:${item.id}`
  }

  const collectionId = Number(item?.collection_id ?? item?.collection?.id)
  return item?.type === NAVIGATION_ITEM_TYPE.COLLECTION && Number.isFinite(collectionId)
    ? `collection:${collectionId}`
    : null
}

export function mergeAutomaticNavigationItems(items, canonicalItems) {
  const currentItems = normalizeNavigationTree(items)
  const normalizedCanonicalItems = normalizeNavigationTree(canonicalItems)
  if (!normalizedCanonicalItems.length) return currentItems

  const canonicalByKey = new Map(
    normalizedCanonicalItems
      .map((item) => [automaticNavigationItemKey(item), item])
      .filter(([key]) => key)
  )
  const currentByKey = new Map(
    currentItems.map((item) => [automaticNavigationItemKey(item), item]).filter(([key]) => key)
  )
  const canonicalKeys = [...canonicalByKey.keys()]
  const shopAllKey = `system:${NAVIGATION_ITEM_TYPE.SHOP_ALL}`
  const occasionsKey = `system:${NAVIGATION_ITEM_TYPE.OCCASIONS}`
  const systemKeys = new Set([shopAllKey, occasionsKey])
  const orderedKeys = [...currentByKey.keys()].filter(
    (key) => canonicalByKey.has(key) || currentByKey.get(key)?.type === NAVIGATION_ITEM_TYPE.LINK
  )

  canonicalKeys.forEach((canonicalKey) => {
    if (systemKeys.has(canonicalKey) || orderedKeys.includes(canonicalKey)) return

    orderedKeys.push(canonicalKey)
  })

  if (!orderedKeys.includes(shopAllKey)) {
    orderedKeys.unshift(shopAllKey)
  }

  if (!orderedKeys.includes(occasionsKey)) {
    const occasionsIndex = canonicalKeys.indexOf(occasionsKey)
    const keysAfterOccasions = new Set(canonicalKeys.slice(occasionsIndex + 1))
    const firstUnavailableIndex = orderedKeys.findIndex((key) => keysAfterOccasions.has(key))
    orderedKeys.splice(
      firstUnavailableIndex >= 0 ? firstUnavailableIndex : orderedKeys.length,
      0,
      occasionsKey
    )
  }

  return normalizeNavigationTree(
    orderedKeys
      .map((key) => {
        const currentItem = currentByKey.get(key)
        const canonicalItem = canonicalByKey.get(key)
        if (!currentItem || !canonicalItem) return currentItem ?? canonicalItem

        const systemType = navigationSystemType(currentItem)
        const usesAutomaticVisibility =
          systemType && currentItem.props?.visibility_mode === NAVIGATION_VISIBILITY_MODE.AUTOMATIC
        if (!usesAutomaticVisibility) return currentItem

        return {
          ...canonicalItem,
          id: currentItem.id,
          label: currentItem.label ?? canonicalItem.label,
          props: {
            ...canonicalItem.props,
            ...currentItem.props,
          },
        }
      })
      .filter(Boolean)
  )
}

export function getNavigationItem(items, itemId) {
  for (const item of Array.isArray(items) ? items : []) {
    if (String(item.id) === String(itemId)) {
      return { item, parentId: null }
    }

    const child = (item.children ?? []).find((candidate) => String(candidate.id) === String(itemId))
    if (child) {
      return { item: child, parentId: item.id }
    }
  }

  return null
}

export function upsertNavigationItem(
  items,
  nextItem,
  {
    previousId = null,
    replacedItemId = null,
    parentId = null,
    preservePosition = true,
    insertAtBeginning = false,
  } = {}
) {
  const itemId = previousId ?? nextItem.id
  const existing = getNavigationItem(items, itemId)
  const previousParentId = existing?.parentId ?? null
  const previousSiblings =
    previousParentId === null
      ? items
      : (getNavigationItem(items, previousParentId)?.item?.children ?? [])
  const previousIndex = previousSiblings.findIndex(
    (candidate) => String(candidate.id) === String(itemId)
  )
  const insertionIndex =
    preservePosition && String(previousParentId) === String(parentId) && previousIndex >= 0
      ? previousIndex
      : null
  const { emptiedGroupIds } = getNavigationMoveConsequences(items, {
    previousId: itemId,
    replacedItemId,
    parentId,
  })
  let nextItems = removeNavigationItem(items, replacedItemId)
  nextItems = removeNavigationItem(nextItems, itemId)

  for (const emptiedGroupId of emptiedGroupIds) {
    nextItems = removeNavigationItem(nextItems, emptiedGroupId)
  }

  const normalizedItem = normalizeNode(nextItem, 0, { child: parentId !== null })

  if (parentId !== null) {
    nextItems = nextItems.map((item) =>
      String(item.id) === String(parentId)
        ? insertIntoGroup(item, normalizedItem, insertionIndex ?? (insertAtBeginning ? 0 : null))
        : item
    )
  } else {
    const targetIndex = insertionIndex ?? (insertAtBeginning ? 0 : nextItems.length)
    nextItems.splice(targetIndex, 0, normalizedItem)
  }

  return normalizeNavigationTree(nextItems)
}

export function getNavigationMoveConsequences(
  items,
  { previousId = null, replacedItemId = null, parentId = null } = {}
) {
  const removedItemIds = new Set(
    [previousId, replacedItemId]
      .filter((itemId) => itemId !== null && typeof itemId !== 'undefined')
      .map(String)
  )
  const sourceParentIds = new Set()

  for (const itemId of removedItemIds) {
    const sourceParentId = getNavigationItem(items, itemId)?.parentId
    if (sourceParentId !== null && typeof sourceParentId !== 'undefined') {
      sourceParentIds.add(sourceParentId)
    }
  }

  const emptiedGroupIds = [...sourceParentIds].filter((sourceParentId) => {
    if (String(sourceParentId) === String(parentId)) return false

    const sourceGroup = getNavigationItem(items, sourceParentId)?.item

    return (
      sourceGroup?.type === NAVIGATION_ITEM_TYPE.MENU &&
      (sourceGroup.children ?? []).every((child) => removedItemIds.has(String(child.id)))
    )
  })

  return { emptiedGroupIds }
}

function insertIntoGroup(group, item, index) {
  const children = [...(group.children ?? [])]
  children.splice(index ?? children.length, 0, item)

  return { ...group, children: normalizeNavigationTree(children, { child: true }) }
}

export function removeNavigationItem(items, itemId) {
  if (itemId === null || typeof itemId === 'undefined') {
    return normalizeNavigationTree(items)
  }

  return normalizeNavigationTree(
    (Array.isArray(items) ? items : [])
      .filter((item) => String(item.id) !== String(itemId))
      .map((item) => ({
        ...item,
        children: (item.children ?? []).filter((child) => String(child.id) !== String(itemId)),
      }))
  )
}

export function removeNavigationItemAndEmptyGroups(items, itemId) {
  const { emptiedGroupIds } = getNavigationMoveConsequences(items, {
    previousId: itemId,
    parentId: null,
  })
  let nextItems = removeNavigationItem(items, itemId)

  for (const emptiedGroupId of emptiedGroupIds) {
    nextItems = removeNavigationItem(nextItems, emptiedGroupId)
  }

  return nextItems
}

export function reorderNavigationItems(
  items,
  activeId,
  overId,
  parentId = null,
  eligibleItemIds = null
) {
  const reorder = (nodes) => {
    const eligibleIds = eligibleItemIds
      ? new Set([...eligibleItemIds].map((itemId) => String(itemId)))
      : null
    const eligibleNodes = eligibleIds
      ? nodes.filter((item) => eligibleIds.has(String(item.id)))
      : nodes
    const activeIndex = eligibleNodes.findIndex((item) => String(item.id) === String(activeId))
    const overIndex = eligibleNodes.findIndex((item) => String(item.id) === String(overId))

    if (activeIndex < 0 || overIndex < 0 || activeIndex === overIndex) {
      return normalizeNavigationTree(nodes, { child: parentId !== null })
    }

    const reordered = [...eligibleNodes]
    const [activeItem] = reordered.splice(activeIndex, 1)
    reordered.splice(overIndex, 0, activeItem)

    if (!eligibleIds) {
      return normalizeNavigationTree(reordered, { child: parentId !== null })
    }

    let reorderedIndex = 0
    return normalizeNavigationTree(
      nodes.map((item) => (eligibleIds.has(String(item.id)) ? reordered[reorderedIndex++] : item)),
      { child: parentId !== null }
    )
  }

  if (parentId === null) {
    return reorder(items)
  }

  return normalizeNavigationTree(
    (Array.isArray(items) ? items : []).map((item) =>
      String(item.id) === String(parentId)
        ? { ...item, children: reorder(item.children ?? []) }
        : item
    )
  )
}

export function navigationItemLabel(item, collectionsById = new Map()) {
  if (item?.type === NAVIGATION_ITEM_TYPE.COLLECTION) {
    return (
      item?.collection?.name ??
      collectionsById.get(Number(item?.collection_id))?.name ??
      'Collection'
    )
  }

  if (item?.type === NAVIGATION_ITEM_TYPE.SHOP_ALL) {
    return item?.label || 'Shop All'
  }

  if (item?.type === NAVIGATION_ITEM_TYPE.OCCASIONS) {
    return item?.label || 'Occasions'
  }

  return item?.label || (item?.type === NAVIGATION_ITEM_TYPE.MENU ? 'Menu group' : 'Custom link')
}

export function findNavigationCollectionItem(items, collectionId) {
  const normalizedCollectionId = Number(collectionId)

  for (const item of Array.isArray(items) ? items : []) {
    if (
      item?.type === NAVIGATION_ITEM_TYPE.COLLECTION &&
      Number(item.collection_id) === normalizedCollectionId
    ) {
      return { item, parentId: null }
    }

    const child = (item.children ?? []).find(
      (candidate) =>
        candidate?.type === NAVIGATION_ITEM_TYPE.COLLECTION &&
        Number(candidate.collection_id) === normalizedCollectionId
    )
    if (child) {
      return { item: child, parentId: item.id }
    }
  }

  return null
}

export function navigationPlacementLabel(items, parentId) {
  if (parentId === null || parentId === TOP_LEVEL_PLACEMENT) {
    return 'Top navigation'
  }

  const parent = getNavigationItem(items, parentId)?.item
  return parent?.label || 'Dropdown menu'
}

export function collectNavigationUsage(items, excludedItemId = null) {
  const collectionIds = new Set()
  const systemTypes = new Set()

  const visit = (nodes) => {
    for (const item of Array.isArray(nodes) ? nodes : []) {
      if (String(item?.id) === String(excludedItemId)) {
        continue
      }

      if (item?.type === NAVIGATION_ITEM_TYPE.COLLECTION) {
        collectionIds.add(Number(item.collection_id))
      }
      const systemType = navigationSystemType(item)
      if (systemType) {
        systemTypes.add(systemType)
      }

      visit(item?.children)
    }
  }

  visit(items)

  return { collectionIds, systemTypes }
}

export function pruneUnusedCollectionVisibilityUpdates(items, visibilityUpdates = []) {
  const usedCollectionIds = collectNavigationUsage(items).collectionIds

  // Publishing a hidden collection is coupled to its menu item; hiding a collection remains a
  // standalone storefront choice even when it is no longer in custom navigation.
  return (Array.isArray(visibilityUpdates) ? visibilityUpdates : []).filter(
    (update) =>
      update?.is_published !== true || usedCollectionIds.has(Number(update?.collection_id))
  )
}
